<template lang="pug">
  .wrap
    page-title.report-title(fontSize='30px' :wrapStyle='{ marginBottom: "10px" }')
      | Your program report:

    time-switcher(v-model='dateRange')

    .overall-chart-wrap
      chart-wrap
        line-chart(:chart-data="statusByDayChartData" :options='statusByDayChartOptions' :height='300')

    .el-table.el-table--fit.el-table--border(v-loading='reports.users.loading')
      .el-table__body-wrapper
        table.el-table__body.table(cellpadding='0' cellspacing='0' border='0')
          tbody
            tr.el-table__row
              th(:style='{ width: "50px" }')
                .cell User

              th.is-center.stat-cell(v-for='course in courseList' :key='course.id' :style='{ width: "90px" }')
                .cell {{ course.title }}

          tr.el-table__row(v-for='entry in reports.users.data' :key='entry.id')
            td
              .cell.user-cell
                .name {{ entry.firstName }} {{ entry.lastName }}
                .email {{ entry.email }}

            td.is-center(v-for='course in entry.courses' :key='course.id')
              .cell
                icon(v-if='course.status === "failed"' name='times-circle' color='darkred' :scale='1.3')
                icon(v-if='course.status === "passed"' name='check-circle' color='green' :scale='1.3')
                icon(v-if='course.status === "in_progress"' name='clock' color='orange' :scale='1.3')

      .empty-results(v-if='!reports.users.loading && !reports.users.data.length && !hasSearchParams')
        | This program has not been assigned to anyone yet.

      .empty-results(v-if='!reports.users.loading && !reports.users.data.length && hasSearchParams')
        | No results found.

</template>

<script>
import _ from "lodash";
import format from "date-fns/format";
import startOfWeek from "date-fns/start_of_week";
import endOfWeek from "date-fns/end_of_week";
import eachDay from "date-fns/each_day";
import ChartWrap from "@/shared/components/ChartWrap";
import CrudMixin from "@/shared/mixins/Crud";

import PageTitle from "@/shared/components/layout/PageTitle";
import TimeSwitcher from "@/brapp/components/shared/TimeSwitcher";
import CircularCounter from "@/shared/components/CircularCounter";
import LineChart from "./LineChart";
import DoughnutChart from "./DoughnutChart";

const formatApiDate = date => format(date, "YYYY-MM-DD");

const DEFAULT_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    display: false
  },
  layout: {
    padding: 20
  },
  scales: {
    yAxes: [
      {
        gridLines: {
          display: true,
          color: "rgba(0, 0, 0, 0.03)"
        }
      }
    ]
  }
};

export default {
  components: {
    PageTitle,
    TimeSwitcher,
    CircularCounter,
    LineChart,
    DoughnutChart,
    ChartWrap
  },
  mixins: [CrudMixin],
  data() {
    const now = Date.now();
    const dateRange = [
      formatApiDate(startOfWeek(now, { weekStartsOn: 1 })),
      formatApiDate(endOfWeek(now, { weekStartsOn: 1 }))
    ];
    return {
      program: null,
      mode: "weekly",
      searchParams: {
        q: "",
        status: ""
      },
      statusByDayChartOptions: DEFAULT_CHART_OPTIONS,
      reports: {
        users: {
          loading: false,
          data: []
        },
        counters: {
          loading: false,
          data: {
            passed: [],
            failed: []
          }
        }
      },
      dateRange,
      pieColours: ["#f45943", "#74c6c0", "#5d79b5", "#f8a523"],
      doughnutChartOptions: {
        responsive: true,
        legend: {
          display: false
        },
        layout: {
          padding: 20
        }
      }
    };
  },
  computed: {
    programId() {
      return this.$route.params.programId;
    },
    courseList() {
      if (!this.reports.users.data.length) {
        return [];
      }
      return this.reports.users.data[0].courses.map(c => ({
        id: c.id,
        title: c.title
      }));
    },
    statusByDayChartData() {
      const currentRange = eachDay(this.dateRange[0], this.dateRange[1]);
      const labels = currentRange.map(d => format(d, "D MMM"));
      const groups = [
        {
          key: "passed",
          color: "#5fc1ce",
          data: this.reports.counters.data.passed
        },
        {
          key: "failed",
          color: "#ed5e3d",
          data: this.reports.counters.data.failed
        }
      ];

      const datasets = groups.map(group => ({
        fill: false,
        pointRadius: 4,
        lineTension: 0,
        borderColor: group.color,
        borderWidth: 2,
        label: group.key,
        data: currentRange.map(date => {
          const formattedDate = format(date, "YYYY-MM-DD");
          const dateEntry = group.data.find(d => d.day === formattedDate);
          return dateEntry ? dateEntry.tot : 0;
        })
      }));
      return { labels, datasets };
    },
    hasSearchParams() {
      return _.some(_.values(this.searchParams));
    }
  },
  watch: {
    dateRange(newVal) {
      this.onRangeChange(newVal);
    }
  },
  methods: {
    loadReports() {
      this.loadUsersReport();
      this.loadCountersReport();
    },
    async loadUsersReport() {
      const params = {
        start: formatApiDate(this.dateRange[0]),
        end: formatApiDate(this.dateRange[1]),
        ...this.searchParams
      };

      this.reports.users.loading = true;

      const data = await this.$store.dispatch("api/sendRequest", {
        method: "get",
        url: `/itrain/reports/programs/detail/users/`,
        params: { program: this.programId, ...params }
      });

      this.reports.users.data = data;
      this.reports.users.loading = false;
    },
    async loadCountersReport() {
      const params = {
        start: formatApiDate(this.dateRange[0]),
        end: formatApiDate(this.dateRange[1]),
        ...this.searchParams
      };

      this.reports.counters.loading = true;

      const data = await this.$store.dispatch("api/sendRequest", {
        method: "get",
        url: `/itrain/reports/programs/detail/counters/`,
        params: { program: this.programId, ...params }
      });

      this.reports.counters.data = data;
      this.reports.counters.loading = false;
    },
    onRangeChange() {
      this.loadReports();
    }
  },
  created() {
    this.loadReports();
  }
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/_mixins.scss";

.stat-cell .cell {
  word-break: normal;
  line-height: 15px;
  padding: 10px 0;
}

.overall-stats {
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 30px;

  .stat-panel {
    flex-basis: calc(25% - 10px);
    background: white;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    .num {
      font-size: 40px;
      margin-bottom: 5px;
    }

    .label {
      font-size: 12px;
    }
  }
  @include MQ(M) {
    flex-wrap: wrap;
    .stat-panel {
      flex-basis: calc(50% - 10px);
      margin-bottom: 20px;
    }
  }
  @include MQ(S) {
    flex-direction: column;
    justify-content: flex-start;
    .stat-panel {
      flex-basis: auto;
      margin-bottom: 10px;
    }
  }
}

.stat-cell .cell {
  word-break: normal;
  line-height: 15px;
  padding: 10px 0;
}

.table {
  width: 100%;
}

.user-cell {
  padding: 15px;

  .email {
    font-size: 13px;
    color: #999;
    margin-top: -8px;
  }
}

.name {
  font-size: 15px;
  margin-bottom: 5px;
}

.email {
  font-size: 13px;
}

.overall-chart-wrap {
  background: white;
  flex: 1;
}

.empty-results {
  padding: 50px 0;
  text-align: center;
}

.results-info {
  margin: 10px 0;
}
</style>
